import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentPasteGoOutlined as PasteIcon,
  MapOutlined as MapIcon,
  PlaceOutlined as PlaceIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { LocationMapPicker } from './LocationMapPicker';
import { CollectionSiteType } from '../../types/collection-site.type';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
} as const;

const formGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 2,
  '& > .full-width': { gridColumn: '1 / -1' },
} as const;

const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const FORM_TABS = [
  'collectionSites.basicInfo',
  'collectionSites.locationInfo',
  'collectionSites.addressInfo',
  'collectionSites.contactInfo',
  'collectionSites.scheduleInfo',
  'common.notes',
] as const;

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

function parseCoordinate(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export type CollectionSiteFormState = {
  longitude: string;
  latitude: string;
  google_maps_url: string;
  area: string;
  status: string;
  data: {
    name: string;
    type: CollectionSiteType;
    contact_info: {
      name: string;
      phone: string;
      email: string;
    };
    schedule: {
      pickup_days: string[];
      pickup_time: string;
    };
    address: {
      street: string;
      city: string;
      state: string;
      country: string;
      postal_code: string;
    };
    notes: string;
    capacity: string;
    operating_hours: string;
    special_instructions: string;
  };
};

export interface CollectionSiteFormDialogProps {
  open: boolean;
  onClose: () => void;
  isEdit: boolean;
  formData: CollectionSiteFormState;
  setFormData: React.Dispatch<React.SetStateAction<CollectionSiteFormState>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onPasteLatLng: () => void;
  onSubmit: () => void;
  /** Self-setup: spotlight the tabbed form sections. */
  tourFormHint?: boolean;
  /** Self-setup: spotlight the create/update submit button. */
  tourSubmitHint?: boolean;
  /** Help "Go to" flow: one tip on the whole form (no tab walk). */
  tourFormSingleStep?: boolean;
  onTourFormNext?: () => void;
  onTourSubmitDone?: () => void;
  onTourDismiss?: () => void;
}

export function CollectionSiteFormDialog({
  open,
  onClose,
  isEdit,
  formData,
  setFormData,
  errors,
  setErrors,
  onPasteLatLng,
  onSubmit,
  tourFormHint = false,
  tourSubmitHint = false,
  tourFormSingleStep = false,
  onTourFormNext,
  onTourSubmitDone,
  onTourDismiss,
}: CollectionSiteFormDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setMapPickerOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (errors.latitude || errors.longitude) {
      setActiveTab(1);
    }
    if (errors.name) {
      setActiveTab(0);
    }
  }, [errors.latitude, errors.longitude, errors.name]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  const handleLocationPick = (latitude: number, longitude: number) => {
    setFormData({
      ...formData,
      latitude: latitude.toFixed(6),
      longitude: longitude.toFixed(6),
    });
    setErrors({ ...errors, latitude: '', longitude: '' });
  };

  const latitudeValue = parseCoordinate(formData.latitude);
  const longitudeValue = parseCoordinate(formData.longitude);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <PlaceIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {isEdit ? t('common.edit') : t('common.add')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {isEdit
                ? t('collectionSites.editDialogTitle')
                : t('collectionSites.addDialogTitle')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
      >
        <DialogContent
          dividers
          sx={{
            px: 0,
            py: 0,
            overflow: 'auto',
            flex: '1 1 auto',
          }}
        >
          <SetupCoachmark
            open={tourFormHint && open && !isEdit}
            fullWidth
            title={t('selfSetup.tour.sitesFormTitle')}
            subtitle={t('selfSetup.tour.sitesFormSubtitle')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => {
              // Walk every form section (tab), then hand off to the submit tip.
              if (!tourFormSingleStep && activeTab < FORM_TABS.length - 1) {
                setActiveTab(activeTab + 1);
                return;
              }
              onTourFormNext?.();
            }}
            onDismiss={() => onTourDismiss?.()}
          >
            <Box>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 2.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 48,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    minHeight: 48,
                  },
                }}
              >
                {FORM_TABS.map((labelKey) => (
                  <Tab key={labelKey} label={t(labelKey)} />
                ))}
              </Tabs>

              <Box sx={{ px: 2.5, pb: 2 }}>
                <TabPanel value={activeTab} index={0}>
              <Box sx={formGridSx}>
                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('collectionSites.siteName')}
                  value={formData.data.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      data: { ...formData.data, name: e.target.value },
                    });
                    setErrors({ ...errors, name: '' });
                  }}
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                />

                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t('common.type')}</InputLabel>
                  <Select
                    value={formData.data.type}
                    label={t('common.type')}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        data: {
                          ...formData.data,
                          type: e.target.value as CollectionSiteType,
                        },
                      })
                    }
                  >
                    {Object.values(CollectionSiteType).map((siteType) => (
                      <MenuItem key={siteType} value={siteType}>
                        {t(`collectionSites.types.${siteType}`, { defaultValue: siteType })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.area')}
                  value={formData.area}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, area: value });
                  }}
                  inputProps={{ inputMode: 'numeric' }}
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.status')}
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Box sx={formGridSx}>
                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.latitude')}
                  value={formData.latitude}
                  onChange={(e) => {
                    setFormData({ ...formData, latitude: e.target.value });
                    setErrors({ ...errors, latitude: '' });
                  }}
                  required
                  error={!!errors.latitude}
                  helperText={errors.latitude}
                  inputProps={{
                    inputMode: 'decimal',
                    pattern: '^-?\\d*(\\.\\d*)?$',
                  }}
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.longitude')}
                  value={formData.longitude}
                  onChange={(e) => {
                    setFormData({ ...formData, longitude: e.target.value });
                    setErrors({ ...errors, longitude: '' });
                  }}
                  required
                  error={!!errors.longitude}
                  helperText={errors.longitude}
                  inputProps={{
                    inputMode: 'decimal',
                    pattern: '^-?\\d*(\\.\\d*)?$',
                  }}
                />

                <Box className="full-width">
                  <FormHelperText sx={{ mb: 1, mx: 0 }}>
                    {t('collectionSites.mapPickerHint')}
                  </FormHelperText>
                  {open && activeTab === 1 && (
                    <LocationMapPicker
                      latitude={latitudeValue}
                      longitude={longitudeValue}
                      onLocationChange={handleLocationPick}
                      height={240}
                    />
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      startIcon={<MapIcon />}
                      onClick={() => setMapPickerOpen(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('collectionSites.pickOnMap')}
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      startIcon={<PasteIcon />}
                      onClick={onPasteLatLng}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('collectionSites.pasteLatLng')}
                    </Button>
                  </Box>
                </Box>

                <TextField
                  className="full-width"
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('collectionSites.googleMapsUrl')}
                  value={formData.google_maps_url}
                  onChange={(e) =>
                    setFormData({ ...formData, google_maps_url: e.target.value })
                  }
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Box sx={formGridSx}>
                <TextField
                  className="full-width"
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('collectionSites.streetAddress')}
                  value={formData.data.address.street}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        address: { ...formData.data.address, street: e.target.value },
                      },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.city')}
                  value={formData.data.address.city}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        address: { ...formData.data.address, city: e.target.value },
                      },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.state')}
                  value={formData.data.address.state}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        address: { ...formData.data.address, state: e.target.value },
                      },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.country')}
                  value={formData.data.address.country}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        address: { ...formData.data.address, country: e.target.value },
                      },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.postalCode')}
                  value={formData.data.address.postal_code}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        address: { ...formData.data.address, postal_code: e.target.value },
                      },
                    })
                  }
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <Box sx={formGridSx}>
                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('collectionSites.contactName')}
                  value={formData.data.contact_info.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        contact_info: {
                          ...formData.data.contact_info,
                          name: e.target.value,
                        },
                      },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.phone')}
                  value={formData.data.contact_info.phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        contact_info: {
                          ...formData.data.contact_info,
                          phone: e.target.value,
                        },
                      },
                    })
                  }
                />

                <TextField
                  className="full-width"
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  type="email"
                  label={t('common.email')}
                  value={formData.data.contact_info.email}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        contact_info: {
                          ...formData.data.contact_info,
                          email: e.target.value,
                        },
                      },
                    })
                  }
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
              <Box sx={formGridSx}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t('collectionSites.pickupDays')}</InputLabel>
                  <Select
                    multiple
                    value={formData.data.schedule.pickup_days}
                    label={t('collectionSites.pickupDays')}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        data: {
                          ...formData.data,
                          schedule: {
                            ...formData.data.schedule,
                            pickup_days: e.target.value as string[],
                          },
                        },
                      })
                    }
                  >
                    {WEEKDAYS.map((day) => (
                      <MenuItem key={day} value={day}>
                        {t(`collectionSites.days.${day}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('collectionSites.pickupTime')}
                  type="time"
                  value={formData.data.schedule.pickup_time}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        schedule: {
                          ...formData.data.schedule,
                          pickup_time: e.target.value,
                        },
                      },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.capacity')}
                  value={formData.data.capacity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, capacity: e.target.value },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('collectionSites.operatingHours')}
                  value={formData.data.operating_hours}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, operating_hours: e.target.value },
                    })
                  }
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
              <Box sx={formGridSx}>
                <TextField
                  className="full-width"
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('collectionSites.specialInstructions')}
                  multiline
                  minRows={2}
                  value={formData.data.special_instructions}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        special_instructions: e.target.value,
                      },
                    })
                  }
                />

                <TextField
                  className="full-width"
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('common.notes')}
                  multiline
                  minRows={2}
                  value={formData.data.notes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, notes: e.target.value },
                    })
                  }
                />
              </Box>
            </TabPanel>
              </Box>
            </Box>
          </SetupCoachmark>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <SetupCoachmark
            open={tourSubmitHint && open && !isEdit}
            title={t('selfSetup.tour.sitesSubmitTitle')}
            subtitle={t('selfSetup.tour.sitesSubmitSubtitle')}
            nextLabel={t('selfSetup.tourDone')}
            onNext={() => onTourSubmitDone?.()}
            onDismiss={() => onTourDismiss?.()}
          >
            <Button
              type="submit"
              variant="contained"
              startIcon={isEdit ? <SaveIcon /> : <AddIcon />}
              sx={{ textTransform: 'none' }}
            >
              {isEdit ? t('common.update') : t('common.create')}
            </Button>
          </SetupCoachmark>
        </DialogActions>
      </Box>

      <Dialog
        open={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>{t('collectionSites.mapPickerTitle')}</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <FormHelperText sx={{ mb: 1.5, mx: 0 }}>
            {t('collectionSites.mapPickerHint')}
          </FormHelperText>
          {mapPickerOpen && (
            <LocationMapPicker
              latitude={latitudeValue}
              longitude={longitudeValue}
              onLocationChange={handleLocationPick}
              height={420}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setMapPickerOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default CollectionSiteFormDialog;
